import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Download, MapPin, RefreshCw, Search, Share2, X } from 'lucide-react';
import { api, API, FALLBACK_IMAGE as FALLBACK } from '../lib/api';
import { useLang } from '../i18n';
import { MonogramPattern } from './Brush';
import { Img } from './Img';
import { ShareBar } from './ShareBar';

export const Thumb = ({ product, className = '' }) => (
  <Img
    src={product?.images?.[0] || FALLBACK}
    alt={product?.title || 'VdE'}
    onError={(e) => {
      e.currentTarget.src = FALLBACK;
    }}
    className={`h-full w-full object-contain p-3 mix-blend-multiply ${className}`}
  />
);

// testo leggibile sul colore della Shade: inchiostro sui fondi chiari, bianco sugli scuri
const inkOn = (hex) => {
  const n = parseInt(String(hex || '#000').replace('#', ''), 16);
  const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return L > 0.18 ? '#221D1A' : '#fff';
};

const Badge = ({ affinity, hex }) => {
  const { t } = useLang();
  const label = t(`build.affinity.${affinity}`);
  const strong = affinity === 'shade' || affinity === 'colore';
  return (
    <span
      className="inline-block px-2 py-1 text-[14px] font-semibold"
      style={
        strong
          ? { backgroundColor: hex, color: inkOn(hex) }
          : { backgroundColor: '#EFE9E0', color: 'rgba(34,29,26,0.8)' }
      }
    >
      {label}
    </span>
  );
};

export const ScorePanel = ({ score, hex, testid = 'score-panel' }) => {
  const { t } = useLang();
  if (!score) return null;
  return (
    <div data-testid={testid} className="border-2 border-inchiostro p-6 md:p-9">
      <div className="flex items-start gap-6">
        <span className="font-display text-6xl leading-none md:text-7xl" style={{ color: hex }} data-testid="score-value">
          {score.score}/{score.max}
        </span>
        <div className="min-w-0">
          <p className="text-[14px] font-bold text-inchiostro/70">
            {t('build.scoreTitle')}
          </p>
          <p className="mt-2 font-display text-xl leading-snug md:text-2xl" data-testid="score-verdict">
            {score.verdict}
          </p>
          <p className="mt-3 hidden text-[13px] leading-relaxed text-inchiostro/60 md:block">
            {t('build.scoreHint')}
          </p>
        </div>
      </div>
      <ul className="mt-8">
        {score.rules.map((r) => (
          <li
            key={r.slug}
            data-testid={`rule-${r.slug}`}
            className="flex items-start gap-4 py-4"
          >
            <span
              className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center"
              style={
                r.ok
                  ? { backgroundColor: hex, color: '#fff' }
                  : { border: '1px solid rgba(34,29,26,0.25)', color: 'rgba(34,29,26,0.6)' }
              }
            >
              {r.ok ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={2.5} />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg leading-snug">{r.label}</p>
              <p className="text-sm text-inchiostro/55">{r.hint}</p>
            </div>
            <span className="shrink-0 text-[14px] font-bold text-inchiostro/70">
              {r.detail}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Picker = ({ slot, hex, current, onPick, onClose }) => {
  const { t } = useLang();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return slot.candidates;
    return slot.candidates.filter(
      (c) => c.title.toLowerCase().includes(n) || (c.collection || '').toLowerCase().includes(n)
    );
  }, [slot, q]);

  return (
    <div data-testid="picker" className="fixed inset-0 z-[var(--z-modal)] overflow-y-auto bg-carta">
      <div className="sticky top-0 z-10 bg-carta">
        <div className="wrap flex items-center justify-between gap-4 py-4">
          <div className="min-w-0">
            <p className="text-[14px] font-bold text-inchiostro/70">
              {t('build.pickerKicker')}
            </p>
            <h2 className="truncate font-display text-2xl">{slot.label}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="picker-close"
            aria-label={t('nav.close')}
            className="grid h-11 w-11 shrink-0 place-items-center border border-inchiostro/20 transition-colors hover:bg-inchiostro hover:text-carta"
          >
            <X size={19} strokeWidth={1.6} />
          </button>
        </div>
        <div className="wrap pb-4">
          <label className="flex items-center gap-3 border border-inchiostro/16 px-3">
            <Search size={15} className="shrink-0 text-inchiostro/40" />
            <input
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('build.searchPlaceholder')}
              data-testid="picker-search"
              className="w-full bg-transparent py-3 text-sm outline-none focus:outline-2 focus:outline-marsala"
            />
          </label>
        </div>
      </div>

      <div className="wrap py-8">
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
          {list.map((c) => (
            <button
              key={c.sku}
              type="button"
              onClick={() => onPick(c.sku)}
              data-testid={`picker-option-${c.sku}`}
              className="group text-left"
            >
              <div
                className={`aspect-square overflow-hidden bg-carta-2 transition-shadow ${
                  current === c.sku ? 'ring-2 ring-offset-2 ring-offset-carta' : ''
                }`}
                style={current === c.sku ? { boxShadow: `inset 0 0 0 2px ${hex}` } : undefined}
              >
                <Thumb product={c} />
              </div>
              <p className="mt-3 font-display text-base leading-snug group-hover:text-marsala">
                {c.title.replace(/,\s*[^,]+$/, '')}
              </p>
              <div className="mt-2">
                <Badge affinity={c.affinity} hex={hex} />
              </div>
            </button>
          ))}
        </div>
        {list.length === 0 && (
          <p className="py-16 font-display text-2xl text-inchiostro/65">{t('common.empty')}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Il configuratore vero e proprio: scelta della Shade, i sei posti, il punteggio
 * di coerenza e il salva/condividi. Estratto da TableBuilder per poter vivere sia
 * sulla pagina dedicata /componi sia dentro l'esperienza /storie/shades, senza
 * duplicare la logica (fetch shade, punteggio con debounce, salvataggio tavola).
 * Lo stato della Shade scelta vive nel query param `shade`, quindi è deep-linkabile
 * e condivisibile da entrambe le pagine.
 */
export const TableComposer = () => {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [shades, setShades] = useState([]);
  const [shade, setShade] = useState(null);
  const [slots, setSlots] = useState([]);
  const [picks, setPicks] = useState({});
  const [score, setScore] = useState(null);
  const [openSlot, setOpenSlot] = useState(null);
  const [share, setShare] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/shades?lang=${lang}`).then((r) => setShades(r.data));
  }, [lang]);

  const load = useCallback(
    async (slug) => {
      setLoading(true);
      setShare(null);
      const { data } = await api.get(`/table/slots?shade=${slug}&lang=${lang}`);
      setShade(data.shade);
      setSlots(data.slots);
      setPicks(Object.fromEntries(data.slots.map((s) => [s.slug, s.suggested])));
      setLoading(false);
    },
    [lang]
  );

  /**
   * La tavola parte già apparecchiata. Senza questo, chi arriva vede solo cinque
   * quadrati di colore e un messaggio: deve indovinare che c'è un gioco sotto.
   * Con una shade preselezionata l'interfaccia si spiega da sé — e rende inutile
   * il tutorial in tre passi che stava sopra. L'URL resta pulito (nessun param
   * scritto in autonomia); `?shade=` continua a vincere quando c'è.
   */
  const startShade = params.get('shade');
  const autoStarted = useRef(false);
  useEffect(() => {
    if (startShade) {
      load(startShade);
      return;
    }
    if (!autoStarted.current && shades.length) {
      autoStarted.current = true;
      load(shades[0].slug);
    }
  }, [startShade, shades, load]);

  const skus = useMemo(() => slots.map((s) => picks[s.slug]).filter(Boolean), [slots, picks]);

  useEffect(() => {
    if (!shade || skus.length === 0) return;
    const id = setTimeout(() => {
      api.post(`/table/score?lang=${lang}`, { shade: shade.slug, skus }).then((r) => setScore(r.data));
    }, 200);
    return () => clearTimeout(id);
  }, [shade, skus, lang]);

  const productOf = (slot) => slot.candidates.find((c) => c.sku === picks[slot.slug]);

  // Navigazione del viaggio: le card sono a tutta larghezza, si va avanti/indietro
  // con le frecce o toccando un pezzo nella striscia in alto. `step` segue lo scroll.
  const journeyRef = useRef(null);
  const [step, setStep] = useState(0);
  const scrollToPiece = (i) => {
    const el = journeyRef.current;
    const card = el?.children[i];
    if (el && card) el.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
  };
  const onJourneyScroll = () => {
    const el = journeyRef.current;
    if (!el) return;
    let best = 0;
    let bd = Infinity;
    for (let i = 0; i < el.children.length; i += 1) {
      const d = Math.abs(el.children[i].offsetLeft - el.scrollLeft);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    setStep(best);
  };

  /**
   * "Rifai la proposta" non poteva funzionare ricaricando gli slot: il backend
   * ordina i candidati per (affinità, titolo) e propone sempre `cands[0]`
   * (table.py:164), quindi la fetch restituiva esattamente gli stessi sei pezzi.
   * Qui si estrae davvero una proposta nuova, escludendo il pezzo attuale così
   * il cambio è sempre visibile, e pescando prima tra i candidati in shade o in
   * tinta: resta una tavola coerente, non un accostamento casuale.
   */
  const reroll = () => {
    setShare(null);
    const inTone = (s) =>
      s.candidates.filter((c) => c.affinity === 'shade' || c.affinity === 'colore');
    const material = (c) => (c.materials || [])[0]?.trim().toLowerCase() || '';

    /**
     * La proposta si costruisce attorno a una materia guida, non pescando a caso
     * in ogni posto: la prima regola del metodo chiede almeno tre pezzi nella
     * stessa materia, e con cinque posti una scelta indipendente per posto la
     * mancava quasi sempre (il punteggio scendeva a 0/4 dopo ogni rilancio).
     * La materia guida cambia a ogni rilancio, quindi le proposte restano varie
     * ma ognuna sta in piedi.
     */
    /**
     * La materia guida va scelta tra quelle che almeno DUE posti possono servire:
     * ancorarsi all'acciaio, che ha solo le posate, non fa coerenza da nessuna
     * parte. Con i dati veri le materie utili sono poche (vetro, gres,
     * porcellana, ceramica), ed e' esattamente su quelle che la tavola tiene.
     */
    const spread = new Map();
    slots.forEach((s) => {
      new Set(inTone(s).map(material).filter(Boolean)).forEach((m) =>
        spread.set(m, (spread.get(m) || 0) + 1)
      );
    });
    const shared = [...spread.entries()].filter(([, n]) => n >= 2).map(([m]) => m);
    const anchor = shared.length ? shared[Math.floor(Math.random() * shared.length)] : '';

    setPicks((prev) =>
      Object.fromEntries(
        slots.map((s) => {
          const tone = inTone(s);
          const base = tone.length ? tone : s.candidates;
          const onAnchor = base.filter((c) => material(c) === anchor);
          // scala di preferenza: materia guida → in tinta → qualsiasi candidato
          for (const pool of [onAnchor, base, s.candidates]) {
            const fresh = pool.filter((c) => c.sku !== prev[s.slug]);
            const from = fresh.length ? fresh : pool;
            if (from.length) {
              return [s.slug, from[Math.floor(Math.random() * from.length)].sku];
            }
          }
          return [s.slug, prev[s.slug]];
        })
      )
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/tables', { shade: shade.slug, skus, lang });
      setShare(data);
      window.setTimeout(
        () => document.getElementById('share-box')?.scrollIntoView({ behavior: 'smooth' }),
        120
      );
    } catch {
      toast.error(t('common.error'));
    }
    setSaving(false);
  };

  const shareUrl = share ? `${window.location.origin}${share.path}` : '';
  const imageUrl = share ? `${API}/tables/${share.code}/image.png?lang=${lang}` : '';

  return (
    <>
      {/* scelta della Shade + il percorso in una riga */}
      <section data-testid="table-builder" className="py-10 md:py-14">
        <div className="wrap">
          {/*
            Una riga al posto del tutorial in tre card: nomina le tre mosse nell'ordine
            (colore, pezzi, punteggio), dice dove sei e restituisce il punteggio mentre
            componi. Spiega, orienta e dà feedback con un elemento invece di cinque.
          */}
          <ol
            data-testid="table-flow"
            className="mb-8 flex items-stretch text-[14px] font-bold"
          >
            <li className="flex flex-1 items-center gap-2.5 py-3 pr-4">
              <span
                className="h-4 w-4 shrink-0 border border-inchiostro/20"
                style={{ backgroundColor: shade?.hex || 'transparent' }}
              />
              <span className="truncate text-inchiostro/65">{t('build.flowColor')}</span>
            </li>
            <li className="flex flex-1 items-center gap-2.5 py-3 pl-4 pr-4">
              <span className="shrink-0 font-display text-base leading-none text-inchiostro">
                {slots.length || '–'}
              </span>
              <span className="truncate text-inchiostro/65">{t('build.flowPieces')}</span>
            </li>
            <li className="flex flex-1">
              <a
                href="#arrivo"
                data-testid="flow-score"
                className="flex w-full items-center gap-2.5 py-3 pl-4 transition-colors hover:text-marsala"
              >
                <span
                  className="shrink-0 font-display text-base leading-none"
                  style={{ color: shade?.hex }}
                >
                  {score ? `${score.score}/${score.max}` : '–'}
                </span>
                <span className="truncate text-inchiostro/65">{t('build.flowScore')}</span>
              </a>
            </li>
          </ol>

          <p className="mb-4 text-[14px] font-bold text-inchiostro/70">
            {t('build.pickShade')}
          </p>
          <div className="grid grid-cols-5 gap-2 md:gap-4">
            {shades.map((s) => {
              const on = shade?.slug === s.slug;
              return (
                <button
                  key={s.slug}
                  type="button"
                  onClick={() => setParams({ shade: s.slug })}
                  data-testid={`build-shade-${s.slug}`}
                  aria-pressed={on}
                  className="group text-left"
                >
                  <div
                    className="relative aspect-[4/5] overflow-hidden transition-transform duration-500 md:aspect-[3/2]"
                    style={{
                      backgroundColor: s.hex,
                      outline: on ? '2px solid #221D1A' : 'none',
                      outlineOffset: '3px',
                    }}
                  >
                    <MonogramPattern className="absolute inset-0" opacity={0.07} />
                    <span className="absolute inset-x-2 bottom-2 hidden font-display text-2xl leading-none text-white md:block">
                      {s.name}
                    </span>
                  </div>
                  <p
                    className={`mt-2 font-display text-[14px] leading-tight md:hidden ${
                      on ? 'text-inchiostro' : 'text-inchiostro/65'
                    }`}
                  >
                    {s.name}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {!shade && (
        <section className="py-16">
          <div className="wrap">
            <p className="font-display text-2xl text-inchiostro/50 md:text-3xl">
              {loading || !shades.length ? t('common.loading') : t('build.emptyState')}
            </p>
          </div>
        </section>
      )}

      {shade && (
        <>
          {/* i sei posti */}
          <section data-testid="table-slots" className="py-12 md:py-20">
            <div className="wrap">
              {/*
                Niente titolo qui: la riga del percorso e la shade selezionata nel
                selettore dicono già in che colore stai componendo, e "La tua tavola"
                era scritto due volte a mezzo schermo di distanza.
                Su mobile la striscia sta su una riga sua e i comandi sotto, così i sei
                pezzi restano tutti visibili invece di finire in uno scroll compresso.
              */}
              <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div
                  className="flex items-center gap-2 overflow-x-auto pb-1 fila"
                  data-testid="table-progress"
                >
                  {slots.map((slot, i) => {
                    const p = productOf(slot);
                    const active = i === step;
                    return (
                      <button
                        key={slot.slug}
                        type="button"
                        onClick={() => scrollToPiece(i)}
                        data-testid={`progress-${slot.slug}`}
                        aria-label={slot.label}
                        className="relative h-11 w-11 shrink-0 overflow-hidden bg-carta-2 transition-transform md:h-14 md:w-14"
                        style={{
                          border: `${active ? 2 : 1}px solid ${p ? shade.hex : 'rgba(34,29,26,0.2)'}`,
                          transform: active ? 'scale(1.06)' : 'none',
                        }}
                      >
                        {p ? (
                          <Thumb product={p} />
                        ) : (
                          <span className="grid h-full place-items-center" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
                  <button
                    type="button"
                    onClick={reroll}
                    data-testid="reset-suggestion-btn"
                    className="inline-flex items-center gap-2 border border-inchiostro/25 px-3 py-2.5 text-[14px] font-bold text-inchiostro transition-colors hover:bg-inchiostro hover:text-carta"
                  >
                    <RefreshCw size={12} strokeWidth={2.2} />
                    {t('build.resetSuggestion')}
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToPiece(Math.max(0, step - 1))}
                    disabled={step === 0}
                    data-testid="journey-prev"
                    aria-label={t('build.prevPiece')}
                    className="grid h-11 w-11 place-items-center border border-inchiostro/25 transition-colors hover:bg-inchiostro hover:text-carta disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-inchiostro"
                  >
                    <ArrowLeft size={17} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollToPiece(Math.min(slots.length - 1, step + 1))}
                    disabled={step === slots.length - 1}
                    data-testid="journey-next"
                    aria-label={t('build.nextPiece')}
                    className="grid h-11 w-11 place-items-center border border-inchiostro/25 transition-colors hover:bg-inchiostro hover:text-carta disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-inchiostro"
                  >
                    <ArrowRight size={17} strokeWidth={2} />
                  </button>
                </div>
              </div>

              {/* il viaggio: un pezzo alla volta, a tutta larghezza */}
              <ol
                ref={journeyRef}
                onScroll={onJourneyScroll}
                data-testid="table-journey"
                className="relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3 fila"
              >
                {slots.map((slot, i) => {
                  const p = productOf(slot);
                  return (
                    <li
                      key={slot.slug}
                      data-testid={`slot-${slot.slug}`}
                      className="flex w-full shrink-0 snap-start flex-col bg-carta-2 p-5 md:flex-row md:items-start md:gap-10 md:p-8"
                    >
                      <div className="md:w-2/5">
                        <p className="text-[14px] font-bold text-marsala-dark">
                          {slot.label}
                        </p>
                        <div className="mt-3 aspect-square overflow-hidden bg-carta-2">
                          {p ? <Thumb product={p} /> : null}
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 md:flex-1 md:pt-6">
                        <p className="font-display text-2xl leading-snug md:text-3xl">
                          {p ? p.title.replace(/,\s*[^,]+$/, '') : t('build.slotEmpty')}
                        </p>
                        {p && (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <Badge affinity={p.affinity} hex={shade.hex} />
                            {p.collection && (
                              <span className="text-[14px] text-inchiostro/70">
                                {p.collection}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="mt-5 flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={() => setOpenSlot(slot)}
                            data-testid={`slot-change-${slot.slug}`}
                            className="inline-flex items-center gap-1.5 bg-inchiostro px-4 py-2.5 text-[14px] font-bold text-carta transition-colors hover:bg-notte"
                          >
                            <RefreshCw size={12} strokeWidth={2.4} />
                            {t('build.change')}
                          </button>
                          {p && (
                            <Link
                              to={`/prodotti/${p.slug}`}
                              data-testid={`slot-sheet-${slot.slug}`}
                              className="inline-flex items-center text-[14px] font-bold text-marsala-dark link-ul"
                            >
                              {t('common.sheet')}
                            </Link>
                          )}
                        </div>
                        <p className="mt-5 max-w-[46ch] text-sm leading-relaxed text-inchiostro/65">
                          {slot.hint}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </section>

          {/* l'arrivo: il punteggio della tavola composta, con enfasi */}
          <section
            id="arrivo"
            data-testid="table-arrival"
            className="border-t-4 py-14 md:py-24"
            style={{ borderColor: shade.hex }}
          >
            <div className="wrap">
              <h2 className="max-w-[20ch] text-[clamp(2rem,4.6vw,3.6rem)] leading-[1.02]">
                {t('build.arrivalTitle')}
              </h2>
              <div className="mt-8 grid gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
                <ScorePanel score={score} hex={shade.hex} />

              <div id="share-box" data-testid="share-box">
                {!share ? (
                  /* Una riga e un bottone: il paragrafo che spiegava il salvataggio
                     diceva quello che il bottone dice già. */
                  <>
                    <p className="max-w-[34ch] text-sm leading-relaxed text-inchiostro/65">
                      {t('build.shareText')}
                    </p>
                    <button
                      type="button"
                      onClick={save}
                      disabled={saving || skus.length === 0}
                      data-testid="save-share-btn"
                      className="btn btn-solid mt-6 disabled:opacity-40"
                    >
                      <Share2 size={14} strokeWidth={2} />
                      {saving ? t('build.saving') : t('build.share')}
                    </button>
                  </>
                ) : (
                  <div className="border-2 border-inchiostro p-5 md:p-7">
                    <h3 className="font-display text-2xl leading-tight md:text-3xl">
                      {t('build.savedTitle')}
                    </h3>
                    <p
                      data-testid="share-link"
                      className="mt-4 break-all py-3 text-sm text-inchiostro/70"
                    >
                      {shareUrl}
                    </p>
                    {/* Social, WhatsApp, mail e copia link: la stessa barra della scheda prodotto */}
                    <ShareBar
                      url={shareUrl}
                      title={t('build.whatsappText')}
                      image={new URL(imageUrl, window.location.origin).href}
                      testid="build-share"
                      className="mt-5"
                    />
                    <a
                      href={`${imageUrl}&download=1`}
                      data-testid="download-image-btn"
                      className="btn btn-ink mt-10"
                    >
                      <Download size={13} strokeWidth={2} />
                      {t('build.download')}
                    </a>
                    <div className="mt-6 overflow-hidden bg-carta-2">
                      <img
                        src={imageUrl}
                        alt={t('build.download')}
                        data-testid="share-image"
                        className="w-full"
                      />
                    </div>
                    <div className="mt-6 flex flex-wrap gap-5">
                      <Link to={share.path} data-testid="open-table-link" className="btn btn-line">
                        {t('build.openTable')}
                      </Link>
                      <button
                        type="button"
                        onClick={() => load(shade.slug)}
                        data-testid="restart-btn"
                        className="text-[14px] font-bold text-inchiostro/70 link-ul"
                      >
                        {t('build.restart')}
                      </button>
                    </div>
                  </div>
                )}
                <Link
                  to="/dove-comprare"
                  data-testid="build-cta-where"
                  className="mt-7 inline-flex items-center gap-2 text-[14px] font-bold text-marsala link-ul"
                >
                  <MapPin size={13} strokeWidth={2.2} />
                  {t('common.findRetailer')}
                </Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {openSlot && (
        <Picker
          slot={openSlot}
          hex={shade.hex}
          current={picks[openSlot.slug]}
          onPick={(sku) => {
            setPicks((p) => ({ ...p, [openSlot.slug]: sku }));
            setShare(null);
            setOpenSlot(null);
          }}
          onClose={() => setOpenSlot(null)}
        />
      )}
    </>
  );
};
